import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable, LoadingSpinner, SearchInput, PageHeader, useDataTableUrl } from '@airclou/ui'
import { ExternalLink, Layers } from 'lucide-react'
import { ErrorState } from '../../components/state/ErrorState'
import { listApps, type App } from '@/api/apps'
import { getAppUrl } from '@/utils/appUrls'
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'

export function AppsPage() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard')
  const [apps, setApps] = useState<App[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Search and sort live in the URL (same pattern as the audit log), so they
  // survive a reload, a shared link and back/forward.
  const { search: searchQuery, onSearchChange, sorting, onSortingChange } = useDataTableUrl()

  // Fetch apps
  const loadApps = async () => {
    try {
      setLoading(true)
      setError(null)

      const apps = await listApps()
      setApps(apps)
    } catch (err: any) {
      setError(err.message || t('apps.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadApps()
  }, [])

  // Filter apps by search
  const filteredApps = apps.filter(app =>
    app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (app.description?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  )

  const getStatusBadge = (status: App['status']) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            {t('common.active')}
          </span>
        )
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            {t('common.inactive')}
          </span>
        )
      case 'maintenance':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            {t('apps.maintenance')}
          </span>
        )
    }
  }

  const columns: any[] = [
    {
      id: 'name',
      header: t('apps.app'),
      size: 400,
      cell: (app: App) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/15 dark:bg-primary/25">
            <Layers className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-medium text-foreground">
              {app.name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
              {app.description}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      header: t('common.status'),
      size: 120,
      cell: (app: App) => getStatusBadge(app.status),
    },
    {
      id: 'created',
      header: t('common.created'),
      size: 140,
      cell: (app: App) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateInTz(app.created_at, timezone)}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 60,
      cell: (app: App) => {
        const appUrl = getAppUrl(app.slug)
        return (
          <div className="flex items-center gap-2">
            {app.status === 'active' && appUrl && (
              <a
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                title={t('common.openApp')}
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title={t('apps.title')} description={t('apps.subtitle')} />

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-sm">
          <SearchInput
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={t('apps.searchApps')}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <ErrorState
          title={t('apps.failedToLoad')}
          description={error}
          onRetry={loadApps}
        />
      ) : (
        <DataTable
          data={filteredApps}
          columns={columns}
          sorting={sorting}
          onSortingChange={onSortingChange}
          getRowHref={(app) => `/apps/${app.id}`}
          emptyMessage={t('apps.noAppsFound')}
        />
      )}
    </div>
  )
}
